import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import axios from "axios";
import Reveal from "@/components/Reveal";
import { INQUIRY_CATEGORIES as CATEGORIES } from "@/lib/site";

const API = process.env.REACT_APP_BACKEND_URL;

const labelCls = "block text-sm font-medium text-navy";
const inputCls =
  "mt-2 w-full bg-white border border-line-strong rounded-md px-4 py-3 text-[15px] text-ink placeholder:text-mist/70 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors";

const TOPIC_SLUGS = {
  "copyright-permissions": "Copyright & Permissions",
  "organizational-licensing": "Organizational Licensing",
  "facilitator-training-certification": "Facilitator Training & Certification",
  "books-workbooks-resources": "Books, Workbooks & Resources",
  "speaking-workshops-partnerships": "Speaking, Workshops & Partnerships",
  other: "Other",
};

const EMPTY_MESSAGE = {
  contactName: "",
  email: "",
  question: "",
  category: "General Question",
};

function formatApiErrorDetail(detail) {
  if (Array.isArray(detail)) {
    return detail
      .map((d) =>
        typeof d === "string" ? d : d.msg || "Please check your entries"
      )
      .join(" ");
  }
  if (typeof detail === "string") return detail;
  return "Your message could not be sent. Please try again.";
}

export default function Ask() {
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState(() => {
    const topic = TOPIC_SLUGS[searchParams.get("topic")];
    return { ...EMPTY_MESSAGE, ...(topic ? { category: topic } : {}) };
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (key) => (e) => {
    setMessage({ ...message, [key]: e.target.value });
  };

  const submitMessage = async (e) => {
    e.preventDefault();
    setServerError("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/ask-inquiries`, message, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.data && res.data.ok) {
        setDone(true);
      } else {
        setServerError("Your message could not be sent. Please try again.");
      }
    } catch (err) {
      setServerError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-16 sm:pt-44">
        <Reveal className="text-center">
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Ask Chaplain Derrick
          </p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
            Questions deserve honest answers.
          </h1>
          <p className="mt-7 max-w-2xl mx-auto text-lg leading-relaxed text-stone2">
            A place to ask questions about serious illness, caregiving,
            hospice, and grief — the things people wonder but rarely get to
            ask. Notes are read personally, and any reply goes to the email
            you share.
          </p>
        </Reveal>
      </section>

      <section
        id="message-chaplain-derrick"
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-28 scroll-mt-24"
        data-testid="ask-message-section"
      >
        <Reveal>
          <div className="max-w-2xl mx-auto">
            <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight text-center">
              A question of your own?
            </h2>

            <div className="mt-8 bg-white border border-line rounded-md p-8 sm:p-10">
              {done ? (
                <div
                  role="status"
                  data-testid="ask-message-confirmation"
                  className="text-center py-6"
                >
                  <Mail className="w-8 h-8 text-gold mx-auto" />
                  <h3 className="mt-5 font-serif text-2xl text-navy">
                    Your message has been received.
                  </h3>
                  <p className="mt-4 text-stone2 leading-relaxed">
                    Thank you for writing. This is not a live chat, so there
                    is no immediate response — but your note will be read
                    personally, and with care.
                  </p>
                  <p className="mt-3 text-stone2 leading-relaxed">
                    If a reply would help, it will go to the email you
                    shared.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submitMessage}
                  data-testid="ask-message-form"
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="ask-name" className={labelCls}>
                        Your name <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="ask-name"
                        type="text"
                        required
                        value={message.contactName}
                        onChange={set("contactName")}
                        data-testid="ask-input-name"
                        className={inputCls}
                        placeholder="First and last"
                      />
                    </div>
                    <div>
                      <label htmlFor="ask-email" className={labelCls}>
                        Email address <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="ask-email"
                        type="email"
                        required
                        value={message.email}
                        onChange={set("email")}
                        data-testid="ask-input-email"
                        className={inputCls}
                        placeholder="you@example.org"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="ask-category" className={labelCls}>
                      What is your inquiry about?{" "}
                      <span className="text-burgundy">*</span>
                    </label>
                    <select
                      id="ask-category"
                      required
                      value={message.category}
                      onChange={set("category")}
                      data-testid="ask-category-select"
                      className={inputCls}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ask-question" className={labelCls}>
                      Your question <span className="text-burgundy">*</span>
                    </label>
                    <textarea
                      id="ask-question"
                      required
                      rows={6}
                      value={message.question}
                      onChange={set("question")}
                      data-testid="ask-textarea-question"
                      className={inputCls}
                      placeholder="What are you wondering about?"
                    />
                  </div>

                  {serverError && (
                    <p
                      className="text-sm text-burgundy bg-burgundy-soft border border-burgundy/20 rounded-md px-4 py-3"
                      role="alert"
                      data-testid="ask-message-error"
                    >
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="ask-submit-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 disabled:opacity-60"
                  >
                    <Mail className="w-4 h-4" />
                    {sending ? "Sending…" : "Send your question"}
                  </button>
                </form>
              )}
            </div>

            <p
              className="mt-6 text-sm text-mist leading-relaxed text-center"
              data-testid="ask-disclaimer"
            >
              Please don&rsquo;t include detailed medical or other sensitive
              personal information in your message. This form is not for
              urgent help or medical advice — for urgent needs, please
              contact your care team or local emergency services.
            </p>

            <div
              className="mt-10 flex flex-wrap items-center justify-center gap-x-7 gap-y-3"
              data-testid="ask-quick-links"
            >
              {[
                { to: "/books", label: "Explore the books" },
                { to: "/group-copies", label: "Request group copies" },
                { to: "/booking", label: "Invite Chaplain Derrick to speak" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`ask-quick-link-${l.to.replace("/", "") || "home"}`}
                  className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold transition-colors duration-200"
                >
                  {l.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
